import SendAnonymousCvModal from '../../components/SendAnonymousCvModal';
import React, { useEffect, useMemo, useState } from "react";
import { Modal, message } from "antd";
import { Download, MapPin, Plus, Search, Ban, X } from "lucide-react";
import * as XLSX from "xlsx";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage as apiMessage } from "../../utils/apiError";
import { candidateName } from "../../utils/candidateName";
import { formatPhone } from "../../utils/formatPhone";
import ClientTabHeading from "./ClientTabHeading";
import { CandidateModal } from "../candidate/CandidateModal";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

const REFUSED = ["Refusé", "Pas intéressé", "Client pas intéressé"];
const geocodeCache = new Map();

async function geocodeAddress(query) {
  const text = (query || "").trim();
  if (!text) return null;
  if (geocodeCache.has(text)) return geocodeCache.get(text);
  try {
    const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(text)}&limit=1`);
    const data = await response.json();
    const coords = data?.features?.[0]?.geometry?.coordinates;
    const result = coords ? { lon: coords[0], lat: coords[1] } : null;
    geocodeCache.set(text, result);
    return result;
  } catch {
    return null;
  }
}

function haversineKm(a, b) {
  if (!a || !b) return null;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

function statusClass(status) {
  if (["Accepté", "Intéressé", "Client intéressé"].includes(status)) return "bg-green-100 text-green-800";
  if (REFUSED.includes(status)) return "bg-red-100 text-red-800";
  if (["Candidat proposé (en attente client)", "Mission proposée (en attente candidat)"].includes(status)) return "bg-blue-100 text-blue-800";
  return "bg-yellow-100 text-yellow-800";
}

function sourceId(candidate) {
  const source = candidate?.sourceCandidate;
  if (!source) return "";
  return typeof source === "object" ? source._id || "" : source;
}

function BlockedCandidates({ client }) {
  const [selected, setSelected] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    axios.get("/tempworker-candidate/freelance", { signal: controller.signal })
      .then(({ data }) => { if (active) setCandidates(Array.isArray(data) ? data : []); })
      .catch((error) => { const detail = apiMessage(error, "Impossible de charger les candidats. Fermez puis réessayez."); if (active && detail) setError(detail); })
      .finally(() => { if (active && !controller.signal.aborted) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [open]);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setListLoading(true);
    setListError("");
    axios.get(`client/${client._id}/blocked-candidates`, { signal: controller.signal })
      .then(({ data }) => { if (active) setSelected(Array.isArray(data) ? data : []); })
      .catch((error) => { const detail = apiMessage(error, "Impossible de charger la liste. Rechargez la page pour réessayer."); if (active && detail) setListError(detail); })
      .finally(() => { if (active && !controller.signal.aborted) setListLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [client._id]);
  const add = async (candidate) => {
    if (busyId || selected.some((row) => row._id === candidate._id)) return;
    setBusyId(candidate._id);
    try {
      await axios.post(`client/${client._id}/block-candidate`, { candidateId: candidate._id, ...(reason.trim() ? { reason: reason.trim() } : {}) });
      setSelected((current) => [...current, { _id: candidate._id, firstname: candidate.firstname, lastname: candidate.lastname, email: candidate.email, addedAt: new Date().toISOString(), reason: reason.trim() }]);
      message.success("Candidat ajouté aux bloqués.");
    } catch (error) {
      message.error(apiMessage(error, "Impossible d’enregistrer le candidat."));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (candidate) => {
    if (busyId) return;
    setBusyId(candidate._id);
    try {
      await axios.post(`client/${client._id}/unblock-candidate`, { candidateId: candidate._id });
      setSelected((current) => current.filter((row) => row._id !== candidate._id));
      message.success("Candidat débloqué.");
    } catch (error) {
      message.error(apiMessage(error, "Impossible de retirer le candidat."));
    } finally {
      setBusyId(null);
    }
  };
  const filtered = candidates.filter((candidate) => candidateName(candidate).toLocaleLowerCase("fr-FR").includes(appliedSearch.trim().toLocaleLowerCase("fr-FR")));
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ClientTabHeading title="Bloqués" description="Retrouvez les candidats que ce client ne souhaite pas recevoir." />
        <button type="button" onClick={() => { setSearch(""); setReason(""); setOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"><Plus size={16} />Bloquer un candidat</button>
      </div>
      {listLoading ? <p className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">Chargement…</p> : listError ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">{listError}</p> : selected.length ? (
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {selected.map((candidate) => (
            <article key={candidate._id} className="flex items-center gap-3 p-4">
              <span className="rounded-lg bg-blue-50 p-2 text-blue-700"><Ban size={20} /></span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">{candidateName(candidate)}</p>
                {candidate.reason && <p className="mt-1 text-sm text-slate-500">Motif : {candidate.reason}</p>}
                {candidate.email && <p className="text-sm text-slate-500">{candidate.email}</p>}
              </div>
              <button type="button" disabled={busyId === candidate._id} onClick={() => remove(candidate)} aria-label={`Débloquer ${candidateName(candidate)}`} title="Débloquer" className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"><X size={16} /></button>
            </article>
          ))}
        </div>
      ) : <p className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">Aucun candidat bloqué pour ce client.</p>}
      <Modal open={open} onCancel={() => setOpen(false)} zIndex={2000} centered width={640} footer={null} title={<ClientTabHeading title="Bloquer un candidat" description={client.companyName || client.denomination} />}>
        <div className="relative mt-5"><Search size={16} className="absolute left-3 top-3 text-slate-400" /><input aria-label="Rechercher un candidat" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nom ou prénom" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm" /><div className="mt-2"><SearchPending pending={searching || loading} label={loading ? "Chargement…" : "Recherche…"} /></div></div>
        <label className="mt-4 block text-sm text-slate-600">Motif (facultatif)<textarea rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Précisez la raison du refus du client" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
        <div className="mt-4 max-h-[50vh] overflow-y-auto">
          {loading ? <p className="p-4 text-sm text-slate-500">Chargement…</p> : error ? <p role="alert" className="p-4 text-sm text-red-700">{error}</p> : filtered.length ? filtered.map((candidate) => {
            const added = selected.some((row) => row._id === candidate._id);
            return <div key={candidate._id} className="flex items-center justify-between gap-3 border-b border-slate-100 py-3"><p className="text-sm font-medium text-slate-900">{candidateName(candidate)}</p><button type="button" disabled={added || Boolean(busyId)} onClick={() => add(candidate)} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 disabled:text-slate-400">{added ? "Déjà bloqué" : "Bloquer"}</button></div>;
          }) : <p className="p-4 text-sm text-slate-500">Aucun candidat trouvé.</p>}
        </div>
      </Modal>
    </section>
  );
}

function PressentiPanel({ client }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [distances, setDistances] = useState({});
  const [pool, setPool] = useState([]);
  const [opened, setOpened] = useState(null);
  const [menuId, setMenuId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [appliedAddSearch, addingSearch] = useDebouncedValue(addSearch);
  const [addError, setAddError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [statusForm, setStatusForm] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [orderTarget, setOrderTarget] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [appliedOrderSearch, searchingOrders] = useDebouncedValue(orderSearch);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [sendingOrderId, setSendingOrderId] = useState("");
  const [cvTarget,setCvTarget] = useState(null);
  const [historyCandidate, setHistoryCandidate] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    axios.get(`available-candidat/by-client/${client._id}`)
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((error) => setError(apiMessage(error, "Impossible de charger les candidats pressentis.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [client._id]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    axios.get("tempworker-candidate/freelance", { signal: controller.signal })
      .then(({ data }) => { if (active) setPool(Array.isArray(data) ? data : []); })
      .catch((error) => { const detail = apiMessage(error, "Impossible de charger vos candidats."); if (active && detail) setAddError(detail); });
    return () => { active = false; controller.abort(); };
  }, [client._id]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!rows.length) return;
      const clientCoord = await geocodeAddress([client.address, client.postCode, client.town].filter(Boolean).join(" "));
      if (!clientCoord || cancelled) return;
      for (const candidate of rows) {
        if (cancelled) break;
        const address = [candidate.address, candidate.postCode, candidate.town].filter(Boolean).join(" ");
        if (!address) {
          setDistances((current) => ({ ...current, [candidate._id]: { km: null } }));
          continue;
        }
        const coord = await geocodeAddress(address);
        if (cancelled) break;
        setDistances((current) => ({ ...current, [candidate._id]: { km: haversineKm(clientCoord, coord) } }));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [rows, client.address, client.postCode, client.town]);

  useEffect(() => {
    if (!orderTarget) return undefined;
    const controller = new AbortController();
    setOrdersLoading(true);
    axios.get("order/freelance", { signal: controller.signal })
      .then(({data}) => {if (!controller.signal.aborted) setOrders(Array.isArray(data) ? data : []);})
      .catch(error => {if (!controller.signal.aborted) {const detail = apiMessage(error, "Impossible de charger les commandes."); if(detail) message.error(detail);}})
      .finally(() => {if (!controller.signal.aborted) setOrdersLoading(false);});
    return () => controller.abort();
  }, [orderTarget]);
  const filteredOrders = orders.filter(order => [order.qualification, order.typeContrat, order.town, order.task].filter(Boolean).join(" ").toLocaleLowerCase("fr-FR").includes(appliedOrderSearch.trim().toLocaleLowerCase("fr-FR")));

  const visible = useMemo(() => {
    const query = appliedSearch.trim().toLocaleLowerCase("fr-FR");
    const sorted = [...rows].sort((a, b) => {
      const refused = Number(REFUSED.includes(a.status)) - Number(REFUSED.includes(b.status));
      if (refused) return refused;
      const left = distances[a._id]?.km;
      const right = distances[b._id]?.km;
      return (left ?? Infinity) - (right ?? Infinity);
    });
    if (!query) return sorted;
    return sorted.filter((candidate) => [candidate.firstname, candidate.lastname, candidate.phone, candidate.email, candidate.town, candidate.postCode, ...(candidate.qualifications || [])].join(" ").toLocaleLowerCase("fr-FR").includes(query));
  }, [rows, appliedSearch, distances]);

  const addable = pool.filter((candidate) => candidateName(candidate).toLocaleLowerCase("fr-FR").includes(appliedAddSearch.trim().toLocaleLowerCase("fr-FR")));

  const addCandidate = async (candidate) => {
    if (busyId) return;
    setBusyId(candidate._id);
    try {
      await axios.post("available-candidat", {
        firstname: candidate.firstname,
        lastname: candidate.lastname,
        phone: candidate.phone,
        email: candidate.email,
        address: candidate.address,
        postCode: candidate.postCode,
        town: candidate.town,
        qualifications: candidate.qualifications || [],
        hourlyRate: candidate.hourlyRate,
        isVehicule: (candidate.locomotions || []).some((item) => ["voiture", "moto"].includes(String(item || "").trim().toLowerCase())) ? "OUI" : "NON",
        clients: [client._id],
        sourceCandidate: candidate._id,
      });
      message.success("Candidat ajouté aux pressentis.");
      setAddOpen(false);
      load();
    } catch (error) {
      message.error(apiMessage(error, "Impossible d’ajouter ce candidat."));
    } finally {
      setBusyId("");
    }
  };

  const saveStatus = async () => {
    if (!statusForm || statusSaving) return;
    setStatusSaving(true);
    try {
      await axios.put(`available-candidat/${statusForm.candidate._id}/client-status`, {
        clientId: client._id,
        status: statusForm.status,
        statusComment: statusForm.comment,
      });
      setRows((current) => current.map((row) => row._id === statusForm.candidate._id ? { ...row, status: statusForm.status, statusComment: statusForm.comment } : row));
      message.success(`Statut mis à jour : ${statusForm.status}`);
      setStatusForm(null);
    } catch (error) {
      message.error(apiMessage(error, "Impossible de mettre à jour le statut."));
    } finally {
      setStatusSaving(false);
    }
  };

  const removeCandidate = async (candidate) => {
    try {
      await axios.put(`available-candidat/${candidate._id}/remove-client`, { clientId: client._id });
      setRows((current) => current.filter((row) => row._id !== candidate._id));
      message.success("Candidat retiré des pressentis de ce client.");
    } catch (error) {
      message.error(apiMessage(error, "Impossible de retirer le candidat."));
    }
  };

  const sendOrder = async (order) => {
    if (!orderTarget || sendingOrderId) return;
    setSendingOrderId(order._id);
    try {
      await axios.post(`available-candidat/${orderTarget._id}/send-order`, { orderId: order._id });
      message.success("Email envoyé au candidat.");
      setOrderTarget(null);
      load();
    } catch (error) {
      message.error(apiMessage(error, "Impossible d’envoyer l’email."));
    } finally {
      setSendingOrderId("");
    }
  };

  const openEmail = candidate => {
    setCvTarget({...candidate,_id:candidate.sourceCandidate?._id || candidate.sourceCandidate || candidate._id});
    setMenuId('');
  };



  const exportExcel = () => {
    if (!rows.length) { message.info("Aucun candidat pressenti à exporter."); return; }
    const sheet = XLSX.utils.json_to_sheet(rows.map((candidate) => ({
      Prénom: candidate.firstname || "",
      Nom: candidate.lastname || "",
      Téléphone: candidate.phone || "",
      Email: candidate.email || "",
      "Code postal": candidate.postCode || "",
      Ville: candidate.town || "",
      "Distance (km)": distances[candidate._id]?.km != null ? Math.round(distances[candidate._id].km) : "",
      "Taux horaire": candidate.hourlyRate ? `${candidate.hourlyRate} €` : "",
      Véhiculé: candidate.isVehicule === "OUI" ? "Oui" : "Non",
      Qualifications: Array.isArray(candidate.qualifications) ? candidate.qualifications.filter(Boolean).join(", ") : "",
      Statut: candidate.status || "En cours",
      "Commentaire statut": candidate.statusComment || "",
    })));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Pressentis");
    const name = (client.companyName || "client").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    XLSX.writeFile(book, `pressentis_${name}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const openProfile = (candidate) => {
    const person = pool.find((row) => String(row._id) === String(sourceId(candidate)));
    if (person) setOpened(person);
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ClientTabHeading title="Pressenti" description={`${rows.length} candidat${rows.length > 1 ? "s" : ""} disponible${rows.length > 1 ? "s" : ""} pour ${client.companyName || "ce client"}.`} />
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={exportExcel} disabled={!rows.length} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 disabled:opacity-40"><Download size={16} />Exporter Excel</button>
          <button type="button" onClick={() => { setAddSearch(""); setAddOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"><Plus size={16} />Ajouter un candidat</button>
        </div>
      </div>
      <div className="relative max-w-md"><Search size={16} className="absolute left-3 top-3 text-slate-400" /><input aria-label="Rechercher un candidat pressenti" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nom, téléphone, email, ville, qualification…" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm" /><div className="mt-2"><SearchPending pending={searching || loading} label={loading ? "Chargement…" : "Recherche…"} /></div></div>
      {loading ? <p className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">Chargement…</p> : error ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">{error}</p> : visible.length ? (
        <div className="app-table-container">
          <table className="app-table" aria-label="Candidats pressentis">
            <thead><tr>{["Candidat", "Téléphone", "Email", "Localisation", "Distance", "Taux", "Véhiculé", "Qualifications", "Statut", "Actions"].map((label) => <th key={label}>{label}</th>)}</tr></thead>
            <tbody>
              {visible.map((candidate) => {
                const distance = distances[candidate._id];
                const person = pool.find((row) => String(row._id) === String(sourceId(candidate)));
                return (
                  <tr key={candidate._id}>
                    <td>
                      {person ? <button type="button" onClick={() => openProfile(candidate)} className="font-semibold text-teal-700 hover:underline">{candidateName(candidate)}</button> : <strong>{candidateName(candidate)}</strong>}
                      {candidate.hasWorkedForClient && <p className="mt-1 text-xs font-medium text-orange-700">Déjà travaillé ici</p>}
                    </td>
                    <td>{formatPhone(candidate.phone) || "—"}</td>
                    <td>{candidate.email || "—"}</td>
                    <td>{[candidate.postCode, candidate.town].filter(Boolean).join(" ") || "—"}</td>
                    <td>{distance === undefined ? "…" : distance.km == null ? "—" : <span className="inline-flex items-center gap-1"><MapPin size={14} />{distance.km < 10 ? distance.km.toFixed(1) : Math.round(distance.km)} km</span>}</td>
                    <td>{candidate.hourlyRate ? `${candidate.hourlyRate} €` : "—"}</td>
                    <td>{candidate.isVehicule === "OUI" ? "Oui" : "Non"}</td>
                    <td>{Array.isArray(candidate.qualifications) && candidate.qualifications.length ? candidate.qualifications.join(", ") : "—"}</td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${statusClass(candidate.status)}`}>{candidate.status || "En cours"}</span>
                      {candidate.statusComment && candidate.status && candidate.status !== "En cours" && <p className="mt-1 max-w-[220px] text-xs text-slate-500">{candidate.statusComment}</p>}
                    </td>
                    <td className="relative">
                      <button type="button" aria-expanded={menuId === candidate._id} onClick={() => setMenuId(menuId === candidate._id ? "" : candidate._id)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700">Actions</button>
                      {menuId === candidate._id && (
                        <div className="absolute right-2 z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1 text-left shadow-lg">
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => openEmail(candidate)}>Proposer au client (email + CV)</button>
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { setOrderTarget(candidate); setOrderSearch(""); setMenuId(""); }}>Envoyer une commande au candidat</button>
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { setHistoryCandidate(candidate); setMenuId(""); }}>Historique des emails</button>
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { setStatusForm({ candidate, status: "Accepté", comment: "" }); setMenuId(""); }}>Marquer comme accepté</button>
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => { setStatusForm({ candidate, status: "Refusé", comment: "" }); setMenuId(""); }}>Marquer comme refusé</button>
                          <button type="button" className="block w-full rounded px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50" onClick={() => { setMenuId(""); removeCandidate(candidate); }}>Retirer de ce client</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">{search ? `Aucun candidat ne correspond à « ${search} ».` : "Aucun candidat disponible pressenti pour ce client."}</p>}

      <Modal open={addOpen} onCancel={() => setAddOpen(false)} zIndex={2000} centered width={640} footer={null} title={<ClientTabHeading title="Ajouter un candidat" description={client.companyName || client.denomination} />}>
        <div className="relative mt-5"><Search size={16} className="absolute left-3 top-3 text-slate-400" /><input aria-label="Rechercher un candidat à pressentir" value={addSearch} onChange={(event) => setAddSearch(event.target.value)} placeholder="Nom ou prénom" className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm" /><SearchPending pending={addingSearch} /></div>
        <div className="mt-4 max-h-[50vh] overflow-y-auto">
          {addError && !pool.length ? <p role="alert" className="p-4 text-sm text-red-700">{addError}</p> : addable.length ? addable.map((candidate) => (
            <div key={candidate._id} className="flex items-center justify-between gap-3 border-b border-slate-100 py-3">
              <p className="text-sm font-medium text-slate-900">{candidateName(candidate)}</p>
              <button type="button" disabled={Boolean(busyId)} onClick={() => addCandidate(candidate)} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-40">{busyId === candidate._id ? "Ajout…" : "Ajouter"}</button>
            </div>
          )) : <p className="p-4 text-sm text-slate-500">Aucun candidat trouvé.</p>}
        </div>
      </Modal>

      <Modal open={Boolean(statusForm)} onCancel={() => !statusSaving && setStatusForm(null)} zIndex={2000} footer={null} title={statusForm?.status === "Accepté" ? "Accepter le candidat" : "Refuser le candidat"}>
        {statusForm && (
          <div className="space-y-4 pt-2">
            <label className="block text-sm text-slate-600">Motif {statusForm.status === "Accepté" ? "de l’acceptation" : "du refus"}
              <textarea rows={4} value={statusForm.comment} onChange={(event) => setStatusForm({ ...statusForm, comment: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" disabled={statusSaving} onClick={() => setStatusForm(null)} className="rounded-lg border px-4 py-2 text-sm">Annuler</button>
              <button type="button" disabled={statusSaving} onClick={saveStatus} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${statusForm.status === "Accepté" ? "bg-green-600" : "bg-red-600"}`}>{statusSaving ? "Enregistrement…" : statusForm.status === "Accepté" ? "Accepter" : "Refuser"}</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(orderTarget)} onCancel={() => setOrderTarget(null)} zIndex={2000} width={760} footer={null} title={orderTarget ? `Envoyer une commande à ${candidateName(orderTarget)}` : "Envoyer une commande"}>
        <p className="mb-3 text-sm text-slate-500">Choisissez une de vos commandes. Son descriptif est envoyé par email au candidat.</p>
        <input aria-label="Rechercher une commande" value={orderSearch} onChange={(event) => setOrderSearch(event.target.value)} placeholder="Poste, ville…" className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /><SearchPending pending={searchingOrders || ordersLoading} />
        <div className="max-h-[420px] space-y-3 overflow-y-auto">
          {ordersLoading ? <p className="py-6 text-center text-sm text-slate-500">Chargement des commandes…</p> : filteredOrders.length ? filteredOrders.map((order) => (
            <article key={order._id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{order.qualification || "Commande"}</p>
                  <p className="mt-1 text-sm text-slate-500">{[order.typeContrat, order.town].filter(Boolean).join(" · ")}</p>
                  {order.task && <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-slate-600">{order.task}</p>}
                </div>
                <button type="button" disabled={Boolean(sendingOrderId)} onClick={() => sendOrder(order)} className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">{sendingOrderId === order._id ? "Envoi…" : "Envoyer"}</button>
              </div>
            </article>
          )) : <p className="py-6 text-center text-sm text-slate-500">Aucune commande trouvée.</p>}
        </div>
      </Modal>

      {cvTarget && <SendAnonymousCvModal candidate={cvTarget} initialClientId={client._id} onClose={()=>setCvTarget(null)} onSent={load} />}

      <Modal open={Boolean(historyCandidate)} onCancel={() => setHistoryCandidate(null)} zIndex={2000} footer={null} title={historyCandidate ? `Historique des emails — ${candidateName(historyCandidate)}` : "Historique"}>
        <div className="max-h-[460px] space-y-3 overflow-y-auto pt-2">
          {!(historyCandidate?.emailHistory || []).length ? <p className="py-8 text-center text-sm text-slate-500">Aucun email envoyé pour ce candidat.</p> : [...historyCandidate.emailHistory].sort((a, b) => new Date(b.date) - new Date(a.date)).map((entry, index) => (
            <article key={index} className="rounded-xl border border-slate-200 p-4 text-sm">
              <p className="font-medium text-slate-900">{entry.type === "client" ? "Au client" : "Au candidat"}{entry.date ? ` · ${new Date(entry.date).toLocaleString("fr-FR")}` : ""}</p>
              {entry.subject && <p className="mt-1">{entry.subject}</p>}
              {entry.to && <p className="text-slate-500">Destinataire : {entry.to}</p>}
            </article>
          ))}
        </div>
      </Modal>
      {opened && <CandidateModal key={opened._id} isOpen onClose={() => setOpened(null)} candidate={opened} candidates={pool} setCandidates={setPool} />}
    </section>
  );
}

export default function ClientProspects({ client, blocked = false }) {
  if (blocked) return <BlockedCandidates client={client} />;
  return <PressentiPanel client={client} />;
}
